import type { WaArgsFor, WaToolArgs, WaToolCommand, WaToolResult } from '@bt-common/interfaces/whatsapp-tool-contracts.interface';
import { waToolError, type WaToolResponse } from '@bt-common/interfaces/whatsapp-tools-commands.interface';
import { reportBridgeTimeout } from './ui-stall-reporter';

export interface PostMessageData { cmd: string, timeoutOverride?: number }

/**
 * The page-context wireHandler resolves with EITHER the raw success value
 * (typed per `WaToolResult<C>`) OR an error envelope produced when a handler
 * throws (see PageMessageService.wireHandler — `{success:false, error,
 * errorType?, errorStack?, errorCause?}`). Modeling the union here is what
 * stops callers from blindly doing `waToolSuccess(result)` and double-wrapping
 * the failure shape (BE#96 root cause). Use `unwrapEngineResult` to project
 * the union into a `WaToolResponse<T>`.
 */
export interface WireErrorEnvelope {
  success: false;
  error?: string;
  errorType?: string;
  errorStack?: string;
  errorCause?: unknown;
}
export type WireResponse<T> = T | WireErrorEnvelope;

// Narrowing guard for WireResponse<T>. Success-branch T (= WaToolResult<C>)
// usually has all-optional fields, so `'success' in r` won't narrow via TS's
// structural inference. Use this guard at every WireResponse call site:
// `if (isWireError(r)) return ...; // r is T here`.
export function isWireError<T>(r: WireResponse<T> | undefined | null): r is WireErrorEnvelope {
  return !!r && typeof r === 'object' && 'success' in r && (r as { success: unknown }).success === false;
}

// Conditional-typed signature: when `cmd` is a known WaToolCommand literal,
// the `data` (or envelope.data) field is constrained to WaToolArgs<C> via
// the central WaToolContractMap — field-name drift between content-script
// sender and page-context receiver is a tsc error at the caller, not a
// silent runtime "data.foo is undefined" (BE#95 pattern). When `cmd` is
// any other string (page → content webhook notifications, DOM scrapers
// without a contract entry), the loose branch accepts arbitrary `data`.
//
// Single overload per call shape so TS can't shadow the contract branch
// with a permissive fallback: the conditional discriminates on the inferred
// C, not on overload resolution order.

// Per-side response wrapping: the content-script→page seam returns the
// wire envelope which may be a WireErrorEnvelope. The shared
// WaArgsFor<C> handles the strict-vs-loose args discriminant; the
// wrapping below is what differs from the backend's callEngineTool
// (which returns the unwrapped result after error mapping).
type PostMessageResponse<C extends string> =
  C extends WaToolCommand ? (WireResponse<WaToolResult<C>> | undefined) : unknown;

// 2-arg form — `(cmd, data, opts?)`.
export function postMessageWithResponse<C extends string>(
  cmd: C,
  data: WaArgsFor<C>,
  opts?: { timeoutOverride?: number; source?: string },
): Promise<PostMessageResponse<C>>;
// Object-form — `({cmd, data?, timeoutOverride?}, source?)`. For contract
// cmds the `data` field is required and shape-checked; for non-contract
// cmds it's optional and untyped.
export function postMessageWithResponse<C extends string>(
  envelope: C extends WaToolCommand
    ? { cmd: C; data: WaToolArgs<C>; timeoutOverride?: number }
    : { cmd: C; data?: unknown; timeoutOverride?: number },
  source?: string,
): Promise<PostMessageResponse<C>>;
export async function postMessageWithResponse(
  cmdOrData: any,
  dataOrSource?: any,
  opts?: { timeoutOverride?: number; source?: string },
): Promise<unknown> {
  const data: PostMessageData & Record<string, any> =
    typeof cmdOrData === 'string'
      ? { cmd: cmdOrData, data: dataOrSource, timeoutOverride: opts?.timeoutOverride }
      : cmdOrData;
  const source: string = typeof cmdOrData === 'string'
    ? (opts?.source ?? 'messageService')
    : (typeof dataOrSource === 'string' ? dataOrSource : 'messageService');
  return postMessageWithResponseImpl(data, source);
}

// #764: set on the first reply from the page bridge. Before that, a timeout is the boot race
// (the page-world listener isn't up yet and window.postMessage doesn't queue), not a stall, so
// it still rejects but isn't reported as a bridge_timeout.
let pageBridgeAnswered = false;

/** #651: has the page-world bridge answered any call in this window yet? Read by the never-answered watch. */
export function hasPageBridgeAnswered(): boolean {
  return pageBridgeAnswered;
}

async function postMessageWithResponseImpl<T>(
  data: PostMessageData & Record<string, any>,
  source: string,
): Promise<T | undefined> {
  let timer: NodeJS.Timeout;
  const isContactQuery = data.cmd === 'waQueryContactsAndChatsTool';
  const PERF = '[PERF][postMessageWithResponse]';
  // performance.now() is sub-ms precise and monotonic — use it instead of Date.now()
  // so we can spot bus contention even when system clock jitter is in play.
  const t0 = performance.now();
  // Per-bus-call enqueue/response logs are useful for perf-debugging but fire
  // dozens of times per second under normal use. Gate behind
  // localStorage.BT_PERF_DIAG=1.
  const perfDiag = typeof localStorage !== 'undefined' && localStorage.getItem('BT_PERF_DIAG') === '1';
  if (perfDiag) console.log(`${PERF} ENQUEUE cmd=${data.cmd} source=${source}`);

  if (isContactQuery) {
    console.log(`[whatsappTools.waQueryContactsAndChatsTool] [4-POST-MSG] Sending postMessage for cmd: ${data.cmd}, data:`, JSON.stringify(data.data || {}));
  }

  return Promise.race([
    new Promise<T>((resolve, reject) => {
      const channel = new MessageChannel();

      // Handle postMessage response (for window.postMessage)
      channel.port1.onmessage = (event: MessageEvent) => {
        const dur = (performance.now() - t0).toFixed(1);
        if (perfDiag) console.log(`${PERF} RESPONSE cmd=${data.cmd} duration=${dur}ms`);
        if (isContactQuery) {
          console.log(`[whatsappTools.waQueryContactsAndChatsTool] [4-POST-MSG] Response RECEIVED for ${data.cmd}:`, {
            eventDataType: typeof event.data,
            isUndefined: event.data === undefined,
            isNull: event.data === null,
            keys: event.data ? Object.keys(event.data) : 'N/A',
            contactsCount: event.data?.contacts?.length ?? 'N/A',
            chatsCount: event.data?.chats?.length ?? 'N/A',
            hasError: !!event.data?.error,
            error: event.data?.error,
            sampleContact: event.data?.contacts?.[0] ? JSON.stringify(event.data.contacts[0]).substring(0, 200) : 'N/A',
            sampleChat: event.data?.chats?.[0] ? JSON.stringify(event.data.chats[0]).substring(0, 200) : 'N/A',
          });
        }
        clearTimeout(timer);
        pageBridgeAnswered = true;
        resolve(event.data);
      };

      // Send the message in the browser context
      try {
        if (typeof window !== "undefined" && window.postMessage) {
          window.postMessage(data, "*", [channel.port2]);
          if (isContactQuery) {
            console.log(`[whatsappTools.waQueryContactsAndChatsTool] [4-POST-MSG] postMessage SENT, waiting for response...`);
          }
        } else {
          const err = new Error(`[postMessageWithResponse] [${source}] No postMessage available for cmd ${data.cmd}`);
          console.error(err.message, data);
          reject(err);
        }
      } catch (e) {
        const err = new Error(`[postMessageWithResponse] [${source}] postMessage failed for cmd ${data.cmd}`, { cause: e as Error });
        console.error(err.message, data, e);
        reject(err);
      }

    }),

    new Promise<never>((_, reject) => {
      const timeoutMs = data?.timeoutOverride || 10000;
      timer = setTimeout(() => {
        const dur = (performance.now() - t0).toFixed(1);
        console.warn(`${PERF} TIMEOUT cmd=${data.cmd} after=${dur}ms (limit=${timeoutMs}ms) source=${source}`);
        if (pageBridgeAnswered) reportBridgeTimeout(String(data.cmd), source, timeoutMs);
        reject(new Error(
          `[postMessageWithResponse] [${source}] Timeout after ${timeoutMs}ms waiting for cmd ${data.cmd}`
        ));
      }, timeoutMs);
    }),
  ]).finally(() => {
    clearTimeout(timer);
  });
}

export async function postMessageToBackgroundWithResponse<T>(
  data: PostMessageData & Record<string, any>,
  source: string = "messageService"
): Promise<T | undefined> {
  let timer: NodeJS.Timeout;

  return Promise.race([
    new Promise<T | undefined>((resolve, reject) => {
      console.debug(`[Message Service] [postMessageToBackgroundWithResponse] [${source}] Send message ${data.cmd}`);

      // Helper to send via window.postMessage
      const sendViaWindowPostMessage = () => {
        if (typeof window !== 'undefined') {
          const channel = new MessageChannel();

          channel.port1.onmessage = (event: MessageEvent) => {
            clearTimeout(timer);
            resolve(event.data);
          };

          window.postMessage(data, "*", [channel.port2]);
          return true;
        }
        return false;
      };

      // First try: Chrome extension context (only if we have a valid runtime ID)
      let chromeRuntimeSucceeded = false;
      if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage && chrome.runtime.id) {
        try {
          const callback = (response: any) => {
            // Check for common errors that indicate no extension is listening
            if (chrome.runtime.lastError) {
              const error = chrome.runtime.lastError.message;
              // The benign cases below ("receiving end does not exist",
              // "Could not establish connection", "message channel closed
              // before a response was received") fire constantly in MV3
              // when the background hands off async — skip the log so the
              // console isn't drowning. Real / unexpected runtime errors
              // still surface at warn.
              const isBenign = !!error && (
                error.includes('receiving end does not exist') ||
                error.includes('Could not establish connection') ||
                error.includes('message channel closed before a response was received')
              );
              if (!isBenign) {
                console.warn(`[Message Service] [${source}] Chrome runtime error: ${error}`);
              }

              if (isBenign) {
                clearTimeout(timer);
                resolve(undefined);
                return;
              }

              resolve(undefined);
            } else {
              clearTimeout(timer);
              resolve(response);
            }
          };

          chrome.runtime.sendMessage(chrome.runtime.id, data, callback);
          chromeRuntimeSucceeded = true;
        } catch (e) {
          // chrome.runtime.sendMessage failed - will fall back to window.postMessage
          console.debug(`[Message Service] [${source}] Chrome runtime.sendMessage failed, falling back to window.postMessage`);
        }
      }

      // Second try: Web page context using window.postMessage
      if (!chromeRuntimeSucceeded) {
        if (!sendViaWindowPostMessage()) {
          console.warn(`[Message Service] [${source}] No messaging method available`, data);
          resolve(undefined);
        }
      }
    }),

    // Changed timeout to resolve instead of reject.
    // Honors a per-call `timeoutOverride` (default 5s). Long-running interactive flows
    // (e.g. Google sign-in via getAuthToken, which only returns a token AFTER the user
    // completes the auth tab — can take 30s+) MUST pass a larger timeout, else this race
    // resolves `undefined` first and the real (late) response is dropped — issue #88.
    new Promise<undefined>((resolve) => {
      timer = setTimeout(() => {
        console.debug(`[Message Service] [${source}] No response received for cmd ${data.cmd}, continuing...`);
        resolve(undefined);
      }, data?.timeoutOverride || 5000);
    }),
  ]).finally(() => {
    clearTimeout(timer);
  });
}

export async function getWaContactInfoFromListItemSelector(uniqueSelector: string) {
  let contactInfo = undefined;
  try {
    contactInfo = await postMessageWithResponse({ cmd: "getWaContactInfoFromListItemSelector", data: { selector: uniqueSelector } });
  } catch (error) {
    console.error("Error fetching contactInfo for uniqueSelector:", uniqueSelector, error);
  }
  return contactInfo;

}

